import React from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Users, Shield, Award, UserCheck, BarChart3, Loader2 } from 'lucide-react';
import { useStatistiquesAvancees } from './useStatistiquesAvancees';
import StatistiquesGraphiques from './StatistiquesGraphiques';

export default function StatistiquesAvancees() {
  const {
    loading, stats, periode, dateDebut, setDateDebut, dateFin, setDateFin,
    handlePeriodeChange, handleAppliquerPeriode,
  } = useStatistiquesAvancees();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Header — Ultra Pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-fuchsia-900 p-6 lg:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(168,85,247,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(217,70,239,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-400 flex items-center justify-center shadow-lg shadow-purple-500/40 flex-shrink-0">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                    Mise à jour en temps réel
                  </span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold">Statistiques par Période</h2>
                <p className="text-purple-100 mt-1 text-sm lg:text-base">
                  {stats?.periode ? `📊 ${stats.periode}` : "Suivez l'évolution de votre plateforme."}
                </p>
              </div>
            </div>
            <div className="w-full xl:w-auto flex flex-col md:flex-row gap-3">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-purple-100 text-xs font-semibold uppercase tracking-wider mb-1 block">Période d&apos;analyse</Label>
                <Select value={periode} onValueChange={handlePeriodeChange}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white backdrop-blur-sm hover:bg-white/15">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jour">Aujourd&apos;hui</SelectItem>
                    <SelectItem value="semaine">Cette semaine</SelectItem>
                    <SelectItem value="mois">Ce mois</SelectItem>
                    <SelectItem value="annee">Cette année</SelectItem>
                    <SelectItem value="personnalise">Période personnalisée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {periode === 'personnalise' && (
                <>
                  <div>
                    <Label className="text-purple-100 text-xs font-semibold uppercase tracking-wider mb-1 block">Début</Label>
                    <DatePickerFr value={dateDebut} onChange={setDateDebut} className="bg-white/10 border-white/20 text-white backdrop-blur-sm w-full" />
                  </div>
                  <div>
                    <Label className="text-purple-100 text-xs font-semibold uppercase tracking-wider mb-1 block">Fin</Label>
                    <DatePickerFr value={dateFin} onChange={setDateFin} className="bg-white/10 border-white/20 text-white backdrop-blur-sm w-full" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAppliquerPeriode} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white border-0 shadow-lg shadow-cyan-500/30">
                      Appliquer
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mini KPIs résumé */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-wider">
                  <Users className="h-3.5 w-3.5" /> Nouveaux membres
                </div>
                <p className="text-3xl font-bold mt-1">{stats.membres?.nouveaux ?? 0}</p>
                <p className="text-xs text-purple-100 mt-0.5">{stats.membres?.evolution_nouveaux >= 0 ? '+' : ''}{stats.membres?.evolution_nouveaux ?? 0}% vs période précédente</p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                  <UserCheck className="h-3.5 w-3.5" /> Validés
                </div>
                <p className="text-3xl font-bold mt-1">{stats.membres?.valides ?? 0}</p>
                <p className="text-xs text-purple-100 mt-0.5">{stats.membres?.evolution_valides >= 0 ? '+' : ''}{stats.membres?.evolution_valides ?? 0}% vs période</p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-cyan-300 text-xs font-semibold uppercase tracking-wider">
                  <Shield className="h-3.5 w-3.5" /> Souscriptions
                </div>
                <p className="text-3xl font-bold mt-1">{stats.souscriptions?.total ?? 0}</p>
                <p className="text-xs text-purple-100 mt-0.5">{stats.souscriptions?.evolution >= 0 ? '+' : ''}{stats.souscriptions?.evolution ?? 0}% évolution</p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-yellow-300 text-xs font-semibold uppercase tracking-wider">
                  <Award className="h-3.5 w-3.5" /> Revenus
                </div>
                <p className="text-3xl font-bold mt-1">{(stats.revenus?.total ?? 0).toLocaleString('fr-FR')} €</p>
                <p className="text-xs text-purple-100 mt-0.5">{stats.revenus?.evolution >= 0 ? '+' : ''}{stats.revenus?.evolution ?? 0}% vs période</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <StatistiquesGraphiques stats={stats} />
    </div>
  );
}
